import { type OutlineType } from '@resurs-internal/mui/components/StyledTextField/StyledTextField';
import { type ClipboardEvent, type KeyboardEvent } from 'react';

import { ValidationResultType } from '../../hooks/useFieldValidation';
import TextInputField from '../TextInputField/TextInputField';

type Props = {
    name: string;
    label?: string;
    suffix?: string;
    /**
     * Injected by Group when this field is one of its children, so the surrounding fields' borders can
     * merge into one visually connected list. Its presence also means Group's own outer margin already
     * spaces the whole list from its siblings, so this field must not add its own vertical margin on top
     * of that -- see the `my` below.
     */
    outline?: OutlineType;
} & (
    | { min: number; max: number; customValidation?: never }
    | {
          min?: never;
          max?: never;
          /**
           * Runs on every keystroke exactly like `min`/`max` validation does -- what it avoids is the
           * clamping. `InputField`'s min/max forces the value to range on every change, which makes a
           * multi-digit target below `min` (e.g. typing "1987" with min={1900}) impossible to type: the first
           * digit is immediately forced up to `min`. Pass this (and omit min/max) for a field whose valid
           * range starts above single digits (e.g. a year), and validate the range here instead of clamping.
           */
          customValidation: (value?: string) => Promise<ValidationResultType>;
      }
);

const NumericField = ({ name, label, min, max, customValidation, suffix, outline, ...rest }: Props) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text');
        if (!/^\d+$/.test(pasted)) {
            e.preventDefault();
        }
    };

    return (
        <TextInputField
            type="number"
            name={name}
            label={label}
            suffix={suffix}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            {...(customValidation ? { validation: { customValidation } } : { min, max })}
            outline={outline}
            inputMode="numeric"
            pattern="[0-9]*"
            sx={{
                ...(outline === undefined ? { my: 1.5 } : {}),
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },
            }}
            {...rest}
        />
    );
};

export default NumericField;
